/**
 * AIGeneratePopover — Lightweight popover for AI generation with optional custom instructions.
 *
 * Wraps any trigger element and floats a textarea + Generate/Cancel pair above it.
 * After calling onGenerate the popover closes automatically.
 */

import { useState, useRef, useEffect } from "react";
import type { KeyboardEvent } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui";
import { useTranslation } from "@/packages/utils";

interface AIGeneratePopoverProps {
  children: React.ReactNode;
  onGenerate: (instructions: string) => void;
  isLoading?: boolean;
  label?: string;
}

export function AIGeneratePopover({
  children,
  onGenerate,
  isLoading = false,
  label,
}: AIGeneratePopoverProps) {
  const { t } = useTranslation("audits");
  const [open, setOpen] = useState(false);
  const [instructions, setInstructions] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-focus textarea when popover opens
  useEffect(() => {
    if (open) {
      // Small delay so Radix finishes mounting before we focus
      const id = setTimeout(() => {
        textareaRef.current?.focus();
      }, 50);
      return () => clearTimeout(id);
    }
    // Reset when popover closes
    if (!open) {
      setInstructions("");
    }
  }, [open]);

  function handleGenerate() {
    onGenerate(instructions);
    setOpen(false);
    setInstructions("");
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!isLoading) handleGenerate();
    }
    if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          className="z-50 w-72 rounded-xl border border-border bg-surface p-4 shadow-lg shadow-black/10 outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2"
        >
          {/* Header */}
          <div className="mb-3">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="text-accent text-[14px]">✦</span>
              <span className="text-[12px] font-semibold text-primary tracking-wide uppercase">
                {t("checklist.ai.popover.customInstructions")}
              </span>
            </div>
            <p className="text-[11px] text-muted">
              {t("checklist.ai.popover.optional")}
            </p>
          </div>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("checklist.ai.popover.placeholder")}
            rows={3}
            className="w-full min-h-[72px] max-h-[120px] resize-none rounded-lg border border-border bg-surface px-3 py-2.5 text-[13px] text-primary placeholder:text-muted outline-none focus:ring-1 focus:ring-accent/50 focus:border-accent/50"
          />

          {/* Footer */}
          <div className="flex justify-end gap-2 mt-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={isLoading}
              className="text-[12px]"
            >
              {t("checklist.ai.popover.cancel")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleGenerate}
              disabled={isLoading}
              className="text-[12px]"
            >
              {isLoading ? (
                <>
                  <Loader2 size={12} className="animate-spin" />
                  {t("checklist.ai.popover.generating")}
                </>
              ) : (
                <>{label ?? t("checklist.ai.popover.generate")} →</>
              )}
            </Button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
